import { z } from "zod";

const nullableString = z.string().nullable();
const optionalString = z.string().optional();
const optionalNullableString = z.string().nullish().transform((value) => value ?? undefined);
const optionalNumber = z.number().nullish().transform((value) => value ?? undefined);
const optionalNullableNumber = z.number().nullish().transform((value) => value ?? null);
const numberWithDefault = (defaultValue: number) => z.number().nullish().transform((value) => value ?? defaultValue);

export class ApiResponseValidationError extends Error {
  constructor(endpoint: string, issues: z.core.$ZodIssue[]) {
    const details = issues.map(({ path, message }) => `${path.join(".") || "response"}: ${message}`).join("; ");
    super(`Invalid API response from ${endpoint}: ${details}`);
    this.name = "ApiResponseValidationError";
  }
}

export const parseApiResponse = <T>(schema: z.ZodType<T>, data: unknown, endpoint: string): T => {
  const result = schema.safeParse(data);
  if (!result.success) throw new ApiResponseValidationError(endpoint, result.error.issues);
  return result.data;
};

export const accessTokenResponseSchema = z.looseObject({
  access_token: z.string().min(1),
});

export const userProfileResponseSchema = z.looseObject({
  id: z.number(),
  email: z.string(),
  is_active: z.boolean(),
  advanced_mode: z.boolean(),
  firstname: nullableString,
  lastname: nullableString,
  created_at: z.string(),
  updated_at: z.string(),
  phone: nullableString,
  theme: z.enum(["LIGHT", "DARK", "SYSTEM"]).nullable(),
  language: z.enum(["ENGLISH", "SPANISH", "en", "es"]).nullable(),
  // Kept optional while older API deployments may expose the internal marker;
  // the user profile contract does not require it.
  deleted_at: nullableString.optional(),
});

export const accountSchema = z.object({
  id: z.number(), name: z.string(), description: optionalNullableString, initial_balance: optionalNumber,
  account_type_id: z.number(), billing_cycle_day: optionalNumber, payment_due_days: optionalNumber, credit_limit: optionalNumber,
  color_id: z.number().nullable().optional(),
});
export const accountColorSchema = z.looseObject({ id: z.number(), name: z.string(), hex: z.string().regex(/^#[\da-fA-F]{6}$/) });
export const accountTypeSchema = z.looseObject({
  id: z.number(), name: z.string(), created_at: z.string(), updated_at: z.string(), deleted_at: nullableString,
});
export const frequentItemsSchema = z.record(z.string(), z.number());
const accountBalanceSchema = z.looseObject({
  monthly_deposit: optionalNumber, monthly_expense: optionalNumber, monthly_balance: optionalNumber,
  account_id: z.number(), account_type_id: z.number(), name: z.string(), description: optionalNullableString,
  credit_limit: optionalNumber, credit_used: optionalNumber, available_credit: optionalNumber,
});
export const accountsBalanceSchema = z.looseObject({
  total_expense: z.number(), total_deposit: z.number(), accounts_balance: z.array(accountBalanceSchema),
});
export const accountDeletionImpactSchema = z.looseObject({
  account_id: z.number(),
  account_name: z.string(),
  transactions_to_delete: z.number(),
  transfers_as_destination_to_delete: z.number(),
  installments_to_delete: z.number(),
  import_rows_to_unlink: z.number(),
});

export const transactionSchema = z.looseObject({
  id: z.number(), account_id: z.number(), destination_account_id: optionalNumber, account: optionalNullableString,
  amount: z.number(), category_id: optionalNumber, tag_id: optionalNumber, type_id: z.number(),
  transaction_date: z.string(), name: z.string(), tag: optionalNullableString, description: optionalNullableString,
  ticket_img: nullableString.optional(), saving_plan_id: optionalNumber, saving_pocket_id: optionalNumber, installment_id: optionalNumber,
});
export const monthlyBalanceSchema = z.looseObject({
  total_deposit: z.number(), total_expense: z.number(), total_balance: z.number(),
  monthly_balances: z.array(z.looseObject({ date: z.string(), balance: z.number(), deposit: z.number(), expense: z.number() })),
});
export const transactionTypeSchema = z.looseObject({ id: z.number(), name: z.string() });
export const repeatedTransactionSchema = z.looseObject({ name: z.string(), trx_count: z.number(), total_amount: z.number() });

export const categoryIconSchema = z.object({ id: z.number().int().positive(), name: z.string(), label: z.string(), group: z.string() });

export const categorySchema = z.looseObject({ icon_id: z.number().int().nullable().optional(), color_id: z.number().nullable().optional(), id: z.number(), name: z.string(), description: optionalNullableString, type: optionalString });
export const tagSchema = categorySchema;
const categoryBalanceItemSchema = z.looseObject({
  icon_id: z.number().int().nullable().optional(),
  color_id: z.number().nullable().optional(),
  id: z.number(), name: z.string(), description: optionalNullableString, category_total_expense: z.number(),
  category_total_deposit: z.number(), percentage_of_total_expense: z.number(), percentage_of_total_deposit: z.number(),
});
export const categoryBalanceSchema = z.looseObject({
  total_deposit: z.number(), total_expense: z.number(), count: z.number(), categories_balance: z.array(categoryBalanceItemSchema),
  // Spend whose category is missing or was deleted. Absent on older API builds, hence the
  // default: the totals above already include it either way.
  uncategorized_total_expense: z.number().default(0), uncategorized_total_deposit: z.number().default(0),
});
const tagBalanceItemSchema = z.looseObject({
  color_id: z.number().nullable().optional(), icon_id: z.number().int().nullable().optional(),
  id: z.number(), name: z.string(), description: optionalNullableString, tag_total_expense: z.number(),
  tag_total_deposit: z.number(), percentage_of_total_expense: z.number(), percentage_of_total_deposit: z.number(),
  // Optional: absent on API builds prior to hj-api#82. TagsView falls back to local derivation.
  percentage_of_tagged_expense: z.number().optional(),
});
export const tagBalanceSchema = z.looseObject({
  total_deposit: z.number(), total_expense: z.number(), count: z.number(), tags_balance: z.array(tagBalanceItemSchema),
  // Spend whose tag is missing or was deleted. Absent on older API builds, hence the
  // default: the totals above already include it either way.
  untagged_total_expense: z.number().default(0), untagged_total_deposit: z.number().default(0),
  // Optional: absent on API builds prior to hj-api#82. TagsView falls back to local derivation.
  tagged_total_expense: z.number().optional(),
});

export const savingFundSchema = z.looseObject({
  id: z.number(), name: z.string(), description: optionalNullableString, initial_amount: optionalNumber, target_amount: z.number(),
  target_date: optionalNullableString, expected_monthly_payment: optionalNumber, amount: optionalNumber,
});
export const savingsBalanceSchema = z.looseObject({
  total_saved: z.number(), monthly_expected: z.number(),
  saving_plans_balance: z.array(z.looseObject({
    id: z.number(), name: z.string(), target_amount: z.number(), initial_amount: optionalNumber, saved_amount: numberWithDefault(0),
    target_date: optionalNullableString, expected_monthly_payment: optionalNumber,
  })),
});

export const installmentSchema = z.looseObject({
  id: z.number(), user_id: z.number(), account_id: z.number(), name: z.string(), created_at: z.string(), updated_at: z.string(),
  initial_amount: z.number(), amount: z.number(), description: nullableString.optional(), interest_rate: optionalNullableNumber,
  start_date: nullableString.optional(), num_payments: z.number().nullable().optional(), done_payments: z.number(), active: z.boolean(),
  completed: z.boolean().nullable().optional(), deleted_at: nullableString.optional(), monthly_payment: z.number().nullable().optional(),
});
const installmentPaymentSchema = z.looseObject({
  id: z.number(), amount: z.number(), transaction_date: z.string(), name: nullableString.optional(), description: nullableString.optional(),
});
export const installmentsBalanceSchema = z.looseObject({
  installments_balance: z.array(z.looseObject({
    id: z.number(), account_id: z.number(), name: z.string(), active: z.boolean(), completed: z.boolean().nullable().optional(),
    initial_amount: z.number().nullable().optional(), amount: z.number(), interest_rate: optionalNullableNumber,
    total_interest: optionalNullableNumber, remaining_interest: optionalNullableNumber, done_amount: z.number(), amount_left: z.number(),
    done_payments: z.number(), done_payments_sum: optionalNumber, monthly_expected: z.number().nullable().optional(), payoff_date: nullableString.optional(),
    start_date: nullableString.optional(), description: nullableString.optional(), num_payments: z.number().nullable().optional(),
    payments: z.array(installmentPaymentSchema),
  })),
  total_monthly_expected: z.number(), total_amount_installment: z.number(), total_amount_pending: z.number(), total_interest_pending: numberWithDefault(0), total_installments: z.number(),
});

export const savingInstitutionSchema = z.looseObject({
  id: z.number(), user_id: z.number(), name: z.string(), active: z.boolean(), description: nullableString.optional(),
});
export const savingPocketSchema = z.looseObject({
  id: z.number(), user_id: z.number(), saving_institution_id: z.number(), name: z.string(), amount: z.number(), anual_rate: z.number(),
  monthly_return: z.number().nullable().optional(), yearly_return: z.number().nullable().optional(), active: z.boolean(),
  freeze_time: z.number().nullable().optional(), freeze_date: nullableString.optional(), description: nullableString.optional(),
  unfreeze_date: nullableString.optional(), return_amount_after_freeze: z.number().nullable().optional(),
});

const wrappedStatusSchema = z.enum(["PENDING", "READY", "INELIGIBLE", "FAILED"]);
const wrappedCardSchema = z.looseObject({
  // Keep this open so a newer backend card can use the UI fallback instead of
  // invalidating the entire report response.
  type: z.string(),
  title: z.string(),
  body: z.string(),
  data: z.record(z.string(), z.unknown()).nullable(),
});
export type WrappedCard = z.infer<typeof wrappedCardSchema>;

export const wrappedResponseSchema = z.looseObject({
  id: z.number(),
  period: z.string(),
  period_start: z.string(),
  period_end: z.string(),
  status: wrappedStatusSchema,
  ineligible_reason: z.string().nullable(),
  cards: z.array(wrappedCardSchema).default([]),
  generated_at: z.string().nullable(),
  view_count: z.number(),
});
export type WrappedResponse = z.infer<typeof wrappedResponseSchema>;

const wrappedListItemSchema = z.looseObject({
  id: z.number(),
  period: z.string(),
  period_start: z.string(),
  period_end: z.string(),
  status: wrappedStatusSchema,
  title: z.string().nullable(),
  generated_at: z.string().nullable(),
  first_viewed_at: z.string().nullable(),
});
export type WrappedListItem = z.infer<typeof wrappedListItemSchema>;

export const wrappedListResponseSchema = z.array(wrappedListItemSchema);
export type WrappedListResponse = z.infer<typeof wrappedListResponseSchema>;
